import { useState, useMemo } from 'react'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../contexts/ThemeContext'

interface WeeklyDataPoint {
  week_number: number
  week_start: string
  week_end: string
  total_count: number
  loan_count: number
}

interface Props {
  data: WeeklyDataPoint[]
  prevYearData: WeeklyDataPoint[]
  year: number
}

const COLORS = {
  loan: '#06b6d4',       // cyan
  prevLoan: '#67e8f9',   // light cyan (prev year)
}

const nbNO = (n: number) =>
  n.toLocaleString('nb-NO', { maximumFractionDigits: 0 })

const pctNO = (n: number) =>
  (n * 100).toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %'

const fmtDate = (d: string) => {
  const parts = d.split('-')
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : d
}

type ChartMode = 'andel' | 'antall' | 'akkumulert'

export default function LoanWeeklyChart({ data, prevYearData, year }: Props) {
  const { t } = useTranslation('components')
  const { isDark } = useTheme()
  const [chartMode, setChartMode] = useState<ChartMode>('andel')
  const prevYear = year - 1

  // Loan data has ~15 day lag — skip last 2 weeks for rates
  const maxWeek = data.length > 0 ? Math.max(...data.map((d) => d.week_number)) : 0
  const loanCutoffWeek = maxWeek - 2

  const prevByWeek = useMemo(() => {
    const map: Record<number, WeeklyDataPoint> = {}
    for (const d of prevYearData) map[d.week_number] = d
    return map
  }, [prevYearData])

  const chartData = useMemo(() => {
    const weekNumbers = data.map((d) => d.week_number).sort((a, b) => a - b)
    const currentByWeek: Record<number, WeeklyDataPoint> = {}
    for (const d of data) currentByWeek[d.week_number] = d

    let accLoan = 0
    let accPrevLoan = 0

    // Bound here: inside the map below, `t` is the week's total count.
    const weekLabel = (n: number) => t('charts.weekLabel', { n })

    return weekNumbers.map((wn) => {
      const cur = currentByWeek[wn]
      const prev = prevByWeek[wn]
      const t = cur?.total_count || 1

      accLoan += cur?.loan_count || 0
      accPrevLoan += prev?.loan_count || 0

      const prevT = prev?.total_count || 1
      const withinCutoff = wn <= loanCutoffWeek

      return {
        week_number: wn,
        label: weekLabel(wn),
        week_start: cur?.week_start || '',
        week_end: cur?.week_end || '',
        total_count: cur?.total_count || 0,
        // Rate (with lag cutoff)
        loan_rate: cur && withinCutoff ? cur.loan_count / t : null,
        prev_loan_rate: prev && prev.total_count > 0 ? prev.loan_count / prevT : null,
        // Counts (with lag cutoff)
        loan_count: withinCutoff ? (cur?.loan_count || 0) : null,
        prev_loan_count: prev?.loan_count ?? null,
        // Accumulated (with lag cutoff)
        acc_loan: withinCutoff ? accLoan : null,
        acc_prev_loan: accPrevLoan > 0 ? accPrevLoan : null,
      }
    })
  }, [data, prevYearData, prevByWeek, loanCutoffWeek, t])

  const axisColor = isDark ? '#94a3b8' : '#64748b'
  const gridColor = isDark ? '#334155' : '#e2e8f0'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null
    const raw = payload[0]?.payload as any

    return (
      <div className="rounded-lg shadow-lg border p-3 text-xs bg-white dark:bg-dark-surface dark:border-dark-border max-w-xs">
        <p className="font-semibold mb-1.5 text-gray-900 dark:text-gray-100">
          {label} ({fmtDate(raw.week_start)} – {fmtDate(raw.week_end)})
        </p>
        {chartMode !== 'akkumulert' && raw.total_count > 0 && (
          <p className="text-gray-600 dark:text-gray-400 mb-2">
            {t('charts.totalRegistrations', { n: nbNO(raw.total_count) })}
          </p>
        )}
        <div className="space-y-1">
          {chartMode === 'andel' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.loan }}>● {t('loanWeeklyChart.loanShareYear', { year })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.loan_rate != null ? pctNO(raw.loan_rate) : '–'}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.prevLoan }}>● {t('loanWeeklyChart.loanShareYear', { year: prevYear })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.prev_loan_rate != null ? pctNO(raw.prev_loan_rate) : '–'}</span>
            </div>
          </>
          )}
          {chartMode === 'antall' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.loan }}>● {t('loanWeeklyChart.withLoanYear', { year })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.loan_count != null ? nbNO(raw.loan_count) : '–'}</span>
            </div>
            {raw.prev_loan_count != null && (
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.prevLoan }}>● {t('loanWeeklyChart.withLoanYear', { year: prevYear })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.prev_loan_count)}</span>
            </div>
            )}
          </>
          )}
          {chartMode === 'akkumulert' && (
          <>
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.loan }}>● {t('loanWeeklyChart.withLoanYear', { year })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{raw.acc_loan != null ? nbNO(raw.acc_loan) : '–'}</span>
            </div>
            {raw.acc_prev_loan != null && (
            <div className="flex justify-between gap-4">
              <span style={{ color: COLORS.prevLoan }}>● {t('loanWeeklyChart.withLoanYear', { year: prevYear })}</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{nbNO(raw.acc_prev_loan)}</span>
            </div>
            )}
          </>
          )}
        </div>
      </div>
    )
  }

  const subtitle: Record<ChartMode, string> = {
    andel: t('loanWeeklyChart.subtitleShare'),
    antall: t('loanWeeklyChart.subtitleCount'),
    akkumulert: t('loanWeeklyChart.subtitleAccumulated', { year, prevYear }),
  }

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {t('loanWeeklyChart.title', { year })}
        </h2>
        <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
          {(['andel', 'antall', 'akkumulert'] as ChartMode[]).map((mode) => (
            <button
              key={mode}
              onClick={() => setChartMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                chartMode === mode
                  ? 'bg-primary-500 text-white'
                  : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
              }`}
            >
              {mode === 'andel' ? t('charts.share') : mode === 'antall' ? t('charts.count') : t('charts.accumulated')}
            </button>
          ))}
        </div>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        {subtitle[chartMode]}
      </p>

      <ResponsiveContainer width="100%" height={420}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="label"
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            interval={Math.max(0, Math.floor(chartData.length / 13) - 1)}
          />

          {chartMode === 'andel' && (
          <YAxis
            yAxisId="ratio"
            orientation="left"
            domain={[0, 'auto']}
            tickFormatter={(v: number) => `${(v * 100).toFixed(0)}%`}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{ value: t('charts.share'), angle: -90, position: 'insideLeft', style: { fill: axisColor, fontSize: 12 } }}
          />
          )}

          {(chartMode === 'antall' || chartMode === 'akkumulert') && (
          <YAxis
            yAxisId="count"
            orientation="right"
            domain={[0, 'auto']}
            tickFormatter={(v: number) => nbNO(v)}
            tick={{ fill: axisColor, fontSize: 11 }}
            tickLine={{ stroke: axisColor }}
            axisLine={{ stroke: axisColor }}
            label={{ value: chartMode === 'akkumulert' ? t('charts.accumulated') : t('charts.count'), angle: 90, position: 'insideRight', style: { fill: axisColor, fontSize: 12 } }}
          />
          )}

          <Tooltip content={<CustomTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12, color: axisColor }} iconType="circle" />

          {/* Andel mode */}
          {chartMode === 'andel' && (
          <>
            <Line yAxisId="ratio" type="monotone" dataKey="loan_rate" name={t('loanWeeklyChart.loanShareYear', { year })} stroke={COLORS.loan} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="ratio" type="monotone" dataKey="prev_loan_rate" name={t('loanWeeklyChart.loanShareYear', { year: prevYear })} stroke={COLORS.prevLoan} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
          </>
          )}

          {/* Antall mode */}
          {chartMode === 'antall' && (
          <>
            <Line yAxisId="count" type="monotone" dataKey="loan_count" name={t('loanWeeklyChart.withLoanYear', { year })} stroke={COLORS.loan} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="count" type="monotone" dataKey="prev_loan_count" name={t('loanWeeklyChart.withLoanYear', { year: prevYear })} stroke={COLORS.prevLoan} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
          </>
          )}

          {/* Akkumulert mode */}
          {chartMode === 'akkumulert' && (
          <>
            <Line yAxisId="count" type="monotone" dataKey="acc_loan" name={t('loanWeeklyChart.withLoanYear', { year })} stroke={COLORS.loan} strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 5 }} />
            <Line yAxisId="count" type="monotone" dataKey="acc_prev_loan" name={t('loanWeeklyChart.withLoanYear', { year: prevYear })} stroke={COLORS.prevLoan} strokeWidth={2} strokeDasharray="4 4" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
          </>
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}
